// fingerhandler.ts ↔ game/fingerhandler.cpp (SimUIForm::FingerHandler — the WI
// playfield finger UI) PLUS the input-side callees it drives, homed here the way
// simdraw.ts homes Simulation.cpp's render arm client-side:
//   - SimUIForm's input slice (game/simui.cpp): HitTestGob, MoveOrAttackOrSelect,
//     SetSelectionTargets, IsSelectionCommand, HasSelectedUnits, ShowUnitMenu,
//     SelectSameUnitTypes — THE command-forming surface (SetSelectionTargets ends
//     in the PROVEN sim's MobileUnitGob.SetTarget, which enqueues the wire-exact
//     kmidMove/kmidAttackCommand into ctx.cmdq).
//   - Simulation.cpp's UI-side methods: FingerHitTest, HitTest, FindVisibleGobs,
//     the SetViewPos map pinning, and the selection mutators (Set/ClearGobSelected,
//     SelectSameUnitTypes) — these call UnitGob.Select on SIM gobs (kfGobSelected
//     is presentation-masked state, mutated exactly as the C++ does).
//   - UnitGob::Hilight's flag arm (game/Unit.cpp:635) — m_wfUnit/m_ff mutations.
// The C++ globals are injected: gsim/ggobm/gpplrLocal/gfGodMode → ctx + sim,
// gevm → evm, ggame.GetPlayfieldSize → GetPlayfieldSize, base::GetTickCount /
// HostGetTickCount → GetTickCount, and the presentation reach (gpsprm sprites,
// gsndm sfx, gpfrmPlace, UnitMenu forms, form scroll) → SimUIHost (noop default).

import type { Event } from './event.ts';
import {
  penDownEvent,
  penDownEvent2,
  penUpEvent,
  penUpEvent2,
  penMoveEvent,
  penMoveEvent2,
  penHoldEvent,
  kfRedrawDirty,
  kfRedrawBeforeTimer,
} from './event.ts';
import { Rect } from './rect.ts';
import { ksfxGuiFormShow } from './uikit.ts';
import { DragRect, Vec2d } from './dragrect.ts';
import type { DPoint } from './dragrect.ts';
import { renderFindGobs, kcpgobVisibleMax } from '../render/simdraw.ts';
import type { SimView } from '../render/simdraw.ts';
import type { GobDrawContext } from '../render/gobdraw.ts';
import { kwcMax, kpcMax } from '../gfx/coords.ts';
import type { SimContext } from '../../../sim/src/context.ts';
import type { Simulation } from '../../../sim/src/sim/simulation.ts';
import {
  kfGobActive,
  kfGobMobileUnit,
  kfGobSelected,
  kfGobStructure,
  kfGobUnit,
  kfGobLayerSelection,
} from '../../../sim/src/gob/gob.ts';
import type { Gob } from '../../../sim/src/gob/gob.ts';
import { gmpDistFromDxy } from '../../../sim/src/gob/unitgob.ts';
import type { UnitGob } from '../../../sim/src/gob/unitgob.ts';
import type { MobileUnitGob } from '../../../sim/src/gob/mobileunit.ts';
import type { StructGob } from '../../../sim/src/gob/struct.ts';
import { kgtMobileHeadquarters, kgtReplicator } from '../../../sim/src/gob/gobtypes.ts';
import { FindNearestFreeTile } from '../../../sim/src/gob/tilesearch.ts';
import { kwcTile, kwcTileHalf } from '../../../sim/src/gob/unitconsts.ts';
import type { MobileUnitConsts, StructConsts } from '../../../sim/src/gob/unitconsts.ts';
import { TcFromWc, WcFromTc, WcTrunc, WcFromTile16ths } from '../../../sim/src/math/coords.ts';
import { s16 } from '../../../sim/src/math/int32.ts';
import { kgidNull } from '../../../sim/src/sm/message.ts';
import { kumTowers, kfUnitHilighted } from '../../../sim/src/player/masks.ts';
import { kbfStructure } from '../../../sim/src/terrain/terrainmap.ts';
import { RadiusFromUnitCount } from '../../../sim/src/trigger/triggeractions.ts';
import { TRect } from '../../../sim/src/types.ts';
import type { WPoint } from '../../../sim/src/types.ts';

// game/fingerhandler.cpp:27-31
const SMALL_DRAG = Math.trunc(kwcTile / 4);
const MEDIUM_DRAG = Math.trunc(kwcTile / 3);
const LARGE_DRAG = Math.trunc(kwcTile / 2);
const QUICK_UP_TICKS = 20;

// ht.h:3698-3699
export const kfPhFinger1Down = 0x8000;
export const kfPhFinger2Down = 0x4000;

// ht.h:3679-3682
export const kfMasSelect = 1;
export const kfMasMove = 2;
export const kfMasAttack = 4;
export const kfMasShowMenu = 8;
// TS-only: a ground target issued as an ATTACK-MOVE (engage en route, then resume).
export const kfMasAttackMove = 16;

// ht.h:766-773 — kwcScrollLeftExtra is the IPHONE titlebar compensation; the web
// build doesn't define IPHONE, so 0 (game/makefile.sdl defines no IPHONE)
const kwcScrollLeftExtra = 0;
export const kwcScrollBorderSize = WcFromTile16ths(5);
export const kwcScrollStepSize = kwcTileHalf;
// Edge auto-scroll historically stepped kwcScrollStepSize (½ tile) once per
// 8-tick poll. CheckScroll now polls every tick and steps this much per tick
// (same average speed, but smooth), scaled by the tick delta passed in.
export const kwcScrollStepPerTick = kwcTileHalf >> 3; // 128/8 = 16 WC/tick

// ht.h:3878-3880 (FingerHandler::State)
const FHS_NONE = 0;
const FHS_HILIGHT = 1;
const FHS_SELECT = 2;
const FHS_DRAG = 3;

// game/fingerhandler.cpp:453
const kcpCloseEnough = 32.0;

// game/simui.cpp:2304
const kctDoubleTapDelay = 100; // TUNE:

// ht.h:5341 (kcpgobVisibleMax — ht.h:5345 — lives in render/simdraw.ts)
const kcpgobMax = 768;

// game/sprite.h:41-52 + game/selectionsprite.h:12-36 (BaseSelectionSprite) — the
// drag rect's carrier; the C++ get/set pass the rect BY VALUE. The Draw arm (the
// rubber-band rendering) is host presentation, not state.
export class BaseSelectionSprite {
  private drc_ = new DragRect();
  private fVisible_ = false;

  SetDragRect(drc: DragRect): void {
    this.drc_ = drc.Clone();
  }
  GetDragRect(): DragRect {
    return this.drc_;
  }
  Show(fShow: boolean): void {
    this.fVisible_ = fShow;
  }
  IsVisible(): boolean {
    return this.fVisible_;
  }
}

/** gpfrmPlace — the structure-placement form (ht.h PlaceStructureForm). */
export interface PlaceFormLike {
  SetPassOnInput(fPassOnInput: boolean): void;
  IsBusy(): boolean;
  UpdatePosition(pevt: Event): void;
  /** PlaceStructureForm::OnPaintSimUI (SimUI.cpp:1505) — M4 (the build UI). */
  OnPaintSimUI?(pbm: unknown): void;
  /** PlaceStructureForm::OnPaintControlsSimUI (SimUI.cpp:1550) — M4. */
  OnPaintControlsSimUI?(pbm: unknown, pupd: unknown): void;
  /** Damage the placement overlay's rects (hatch outline + the ✓/✗ buttons) —
   *  SimUIForm.OnUpdateMapInvalidate calls it once per PAINT while placing (see
   *  the placement arm there). */
  InvalidatePlacementRects?(): void;
}

/** The presentation seams the C++ reaches via globals — all fire-and-forget
 *  (M3-presentation; nothing the input pipeline branches on except the place
 *  form and the selection sprite's stored state). */
export interface SimUIHost {
  /** gpsprm->CreateSelectionSprite() (fingerhandler.cpp:50). */
  CreateSelectionSprite(): BaseSelectionSprite | null;
  /** gsndm.PlaySfx; `sfxc:<n>` marks SfxFromCategory(n) (misc.cpp:1127 — the
   *  async-rng variant pick is the host's). */
  PlaySfx(sfx: number | string): void;
  /** UnitGob::Hilight's m_panispr = CreateHilightSprite() (Unit.cpp:641). */
  CreateHilightSprite(punt: UnitGob): void;
  /** UnitGob::Hilight's delete m_panispr (Unit.cpp:656). */
  DestroyHilightSprite(punt: UnitGob): void;
  /** ShowUnitTitle's UnitMenu new+Init+SetOwner+Show (fingerhandler.cpp:679-690);
   *  return null on failure (collapses the C++'s alloc/Init failure arms). */
  CreateUnitTitle(pgob: Gob): unknown | null;
  /** delete m_pfrmUnitTitle (fingerhandler.cpp:57/399). */
  DestroyUnitTitle(pfrm: unknown): void;
  /** pgob->PopupMenu() (simui.cpp:2517). */
  PopupMenu(pgob: Gob): void;
  /** gpfrmPlace (NULL when no placement is active). */
  GetPlaceForm(): PlaceFormLike | null;
  /** gpfrmmSim->Scroll(dx, dy) (Simulation.cpp:1019). */
  Scroll(dx: number, dy: number): void;
  /** gpupdSim->Reset() + gpdisp->ResetScrollOffset() (Simulation.cpp:1005-1006 —
   *  the SetViewPos fInit arm's level-load scroll-bookkeeping reset). */
  ResetScroll(): void;
  /** m_pfrmm->GetUpdateMap()->InvalidateTile (simui.cpp:2342). */
  InvalidateTile(tx: number, ty: number): void;
  /** m_aniMoveTarget.Start(0, 0, 0) (simui.cpp:2343) — the move-target flourish. */
  StartMoveTargetAnim(): void;
}

export function defaultSimUIHost(): SimUIHost {
  return {
    CreateSelectionSprite: () => new BaseSelectionSprite(),
    PlaySfx: () => {},
    CreateHilightSprite: () => {},
    DestroyHilightSprite: () => {},
    CreateUnitTitle: () => null,
    DestroyUnitTitle: () => {},
    PopupMenu: () => {},
    GetPlaceForm: () => null,
    Scroll: () => {},
    ResetScroll: () => {},
    InvalidateTile: () => {},
    StartMoveTargetAnim: () => {},
  };
}

// game/Unit.cpp:635-658 (UnitGob::Hilight) — presentation-masked flag state on
// the SIM gob (kfUnitHilighted in m_wfUnit, kfGobLayerSelection in m_ff),
// mutated exactly; the hilight sprite itself (m_panispr) is host presentation.
export function UnitGobHilight(punt: UnitGob, fHilight: boolean, host: SimUIHost): void {
  if (fHilight) {
    if (punt.m_wfUnit & kfUnitHilighted) {
      return;
    }
    punt.m_wfUnit |= kfUnitHilighted;
    punt.m_ff = (punt.m_ff | kfGobLayerSelection) >>> 0;
    host.CreateHilightSprite(punt); // m_panispr = CreateHilightSprite()

    // Only need to do this so the sprite captures the current frame
    punt.MarkRedraw();
  } else {
    if (!(punt.m_wfUnit & kfUnitHilighted)) {
      return;
    }
    punt.m_wfUnit &= ~kfUnitHilighted;
    if ((punt.m_ff & kfGobSelected) === 0) {
      punt.m_ff = (punt.m_ff & ~kfGobLayerSelection) >>> 0;
    }
    host.DestroyHilightSprite(punt); // delete m_panispr
  }
}

// game/GameObjects.cpp:394-398 (Gob::GetUIBounds — empty) + game/Unit.cpp:566-576
// (UnitGob::GetUIBounds — m_puntc->wrcUIBounds at the gob position), the only
// override; the C++ virtual becomes a structural dispatch on kfGobUnit.
export function GobGetUIBounds(pgob: Gob, pwrc: Rect): void {
  if (pgob.GetFlags() & kfGobUnit) {
    const punt = pgob as UnitGob;
    const b = punt.GetConsts().wrcUIBounds;
    pwrc.left = punt.m_wx + b.left;
    pwrc.top = punt.m_wy + b.top;
    pwrc.right = punt.m_wx + b.right;
    pwrc.bottom = punt.m_wy + b.bottom;
  } else {
    pwrc.SetEmpty();
  }
}

// game/Simulation.cpp:686-695
export function SimulationClearGobSelection(ctx: SimContext): void {
  // Deselect any selected Gobs

  for (let pgobT = ctx.gobm.GetFirstGob(); pgobT !== null; pgobT = ctx.gobm.GetNextGob(pgobT)) {
    const ff = pgobT.GetFlags();
    if ((ff & (kfGobSelected | kfGobUnit)) === (kfGobSelected | kfGobUnit)) {
      (pgobT as UnitGob).Select(false);
    }
  }
}

// game/Simulation.cpp:697-702
export function SimulationSetGobSelected(ctx: SimContext, pgob: Gob): void {
  SimulationClearGobSelection(ctx);
  if ((pgob.GetFlags() & (kfGobSelected | kfGobUnit)) === kfGobUnit) {
    (pgob as UnitGob).Select(true);
  }
}

// game/Simulation.cpp:704-727 (ggobm.FindGobs over a TRect = GobMgr.FindGobsT)
export function SimulationSelectSameUnitTypes(ctx: SimContext, punt: UnitGob, ptrc: TRect): void {
  const pplr = punt.GetOwner();
  if (pplr !== ctx.plrLocal) {
    return;
  }

  const ut = punt.GetUnitType();

  // Store the gobs on the stack.
  const apgob: Gob[] = [];

  const cpgob = ctx.gobm.FindGobsT(ptrc, apgob, Math.trunc(kcpgobMax / 2));
  for (let i = 0; i < cpgob; i++) {
    const pmuntT = apgob[i] as MobileUnitGob;
    const ff = pmuntT.GetFlags();
    if (!(ff & kfGobMobileUnit)) {
      continue;
    }
    if (pmuntT.GetOwner() !== pplr) {
      continue;
    }
    if (pmuntT.GetUnitType() !== ut) {
      continue;
    }

    pmuntT.Select(true);
  }
}

/** The injected globals (see the header comment). */
export interface SimUIDeps {
  ctx: SimContext;
  sim: Simulation;
  /** the render context — FindVisibleGobs' FindGobs edge-clipping needs the
   *  per-gob clipping bounds (anim/bitmap metrics) + the CoordMap. */
  gctx: GobDrawContext;
  /** gevm (SetRedrawFlags only). */
  evm: { SetRedrawFlags(wf: number): void };
  /** base::GetTickCount / HostGetTickCount (1/100s ticks). */
  GetTickCount: () => number;
  /** ggame.GetPlayfieldSize (playfield pixel dims). */
  GetPlayfieldSize: () => { cx: number; cy: number };
  host?: Partial<SimUIHost>;
}

// The SimUIForm INPUT slice (game/simui.cpp) + the Simulation UI-side methods it
// reaches through gsim (game/Simulation.cpp). The rest of SimUIForm (the form
// shell, lockstep pump, HUD) is a later port.
export class SimUIForm {
  // mutable: the replay viewer's backward-seek Rebind()s this slice onto a freshly-restored Sim
  ctx: SimContext;
  sim: Simulation;
  readonly gctx: GobDrawContext;
  readonly evm: { SetRedrawFlags(wf: number): void };
  readonly GetTickCount: () => number;
  readonly GetPlayfieldSize: () => { cx: number; cy: number };
  readonly host: SimUIHost;

  /** game/game.cpp:53 gnScrollSpeed (the prefs slider writes 1.0 + n*0.25). */
  gnScrollSpeed = 1.0;

  // SimUIForm move-target presentation state (ht.h:3936-3938; drawn by OnPaint, M3)
  m_wxMoveTarget = 0;
  m_wyMoveTarget = 0;
  m_nStateMoveTarget = 0;
  // TS-only: the current move-target flourish is an ATTACK-MOVE (drawn red-tinted).
  m_fMoveTargetAttack = false;
  // TS-only: attack-move is ARMED (HUD toggle / tap-hold) — the next ground tap issues
  // an attack-move instead of a plain move. The HUD button reflects this.
  m_fAttackMoveArmed = false;

  // MoveOrAttackOrSelect's function statics (simui.cpp:2312-2313) — per-form
  // instance state here (no module-level mutable state)
  private s_tLastSelected = 0;
  private s_pgobLastSelected: Gob | null = null;

  // Simulation's visible-gob cache (ht.h:5419 m_apgobVisible[kcpgobVisibleMax] +
  // m_wxViewSave/m_wyViewSave/m_cUpdatesSave; zero-init like the C++ global gsim)
  private m_apgobVisible: Gob[] = [];
  // Per-frame visible-gob DRAW cap. Defaults to the global kcpgobVisibleMax (768,
  // the engine gob-slot ceiling); kept as an instance field so the draw pass and
  // OnUpdateMapInvalidate share ONE value.
  cpgobVisibleMax = kcpgobVisibleMax;
  private m_wxViewSave = 0;
  private m_wyViewSave = 0;
  private m_cUpdatesSave = 0;

  /** the SimView renderFindGobs reads (gid map walk; bounds passed explicitly). Rebuilt by Rebind(). */
  private viewForFind: SimView;

  constructor(deps: SimUIDeps) {
    this.ctx = deps.ctx;
    this.sim = deps.sim;
    this.gctx = deps.gctx;
    this.evm = deps.evm;
    this.GetTickCount = deps.GetTickCount;
    this.GetPlayfieldSize = deps.GetPlayfieldSize;
    this.host = { ...defaultSimUIHost(), ...(deps.host ?? {}) };

    this.viewForFind = this.buildViewForFind();
  }

  private buildViewForFind(): SimView {
    const dims = this.ctx.gobm.GetMapSize();
    return {
      gobm: this.ctx.gobm as unknown as SimView['gobm'],
      fogBytes: this.ctx.fogm!.GetMapPtr(),
      ctxMap: dims.ctx,
      ctyMap: dims.cty,
      trMapBytes: this.ctx.trmap!.GetMapPtr(),
      wxView: 0, // unused by renderFindGobs (it takes rcBounds)
      wyView: 0,
      plrLocal: this.ctx.plrLocal,
      apuntc: this.ctx.unitConsts as unknown as SimView['apuntc'],
    };
  }

  /** Re-point this spectator input slice at a NEW Sim (the replay viewer rebuilds a fresh Sim on a
   *  backward-seek restore). A new Sim allocates new fog/terrain/gob arrays, so viewForFind must be
   *  rebuilt; the visible-gob + last-selected caches hold old-sim refs, so clear them too. */
  Rebind(ctx: SimContext, sim: Simulation): void {
    this.ctx = ctx;
    this.sim = sim;
    this.viewForFind = this.buildViewForFind();
    this.m_apgobVisible = [];
    this.m_cUpdatesSave = -1; // force FindVisibleGobs to recompute even at an identical update/view
    this.s_pgobLastSelected = null;
  }

  /** Editor-only: drop the FindVisibleGobs cache so the NEXT call recomputes even at the same view
   *  and update count. The shipped game never needs this (its sim steps, so GetUpdateCount advances
   *  and the cache self-invalidates); the WYSIWYG editor holds the sim UNSTEPPED, so a same-view
   *  edit (drag-poke / rebuild) would otherwise redraw from a stale visible-gob list. Mirrors the
   *  reset Rebind already does. */
  InvalidateVisibleGobs(): void {
    this.m_cUpdatesSave = -1;
  }

  // game/Simulation.cpp:1037-1045
  GetViewPos(pwpt: WPoint): void {
    pwpt.wx = this.sim.m_wxView;
    pwpt.wy = this.sim.m_wyView;
  }

  // game/Simulation.cpp:968-1035 — the map-bounds pinning + scroll plumbing the
  // TS sim's bare SetViewPos doesn't carry (presentation-simplified there); homed
  // here client-side. fInit (Simulation.cpp:1004-1010, the level-load arm) resets
  // the scroll bookkeeping and re-homes the view at (0,0) so the common path
  // SCROLLS to the target — that scroll is what aligns the sim update map's
  // map-tile coordinate space (UpdateMap m_txOrigin/m_xMapOffset) with the view.
  SetViewPos(wx: number, wy: number, fInit = false): boolean {
    // Pin the passed position to the bounds of the map

    const c = this.gctx.coords;
    // m_plvl->GetTileMap()->GetMapSize() — tile-map PIXEL dims (tiles * tile size)
    const dims = this.ctx.gobm.GetMapSize();
    const sizMap = { cx: dims.ctx * c.gcxTile, cy: dims.cty * c.gcyTile };

    const sizPlayfield = this.GetPlayfieldSize();

    let wcxMax: number, wcyMax: number;
    const cxMax = sizMap.cx - sizPlayfield.cx;
    const cyyMax = sizMap.cy - sizPlayfield.cy;

    if (cxMax <= 0 || cxMax > kpcMax) {
      wcxMax = 0;
    } else {
      wcxMax = c.WcFromUpc(cxMax);
    }

    if (cyyMax <= 0 || cyyMax > kpcMax) {
      wcyMax = 0;
    } else {
      wcyMax = c.WcFromUpc(cyyMax);
    }

    if (wx < 0) {
      wx = 0;
    } else if (wx > wcxMax) {
      wx = wcxMax;
    }

    if (wy < 0) {
      wy = 0;
    } else if (wy > wcyMax) {
      wy = wcyMax;
    }

    // If we're initing, we're loading a new level. Invalidate and
    // set the map offset

    if (fInit) {
      this.host.ResetScroll(); // gpupdSim->Reset(); gpdisp->ResetScrollOffset()
      this.sim.SetViewPos(0, 0); // m_wxView = m_wyView = WcFromPc(0)
    }

    if (this.sim.m_wxView === wx && this.sim.m_wyView === wy) {
      return false;
    }

    // Otherwise scroll the map

    // Pixel-level scroll: the TRUE pixel delta (the old `& 0xfffe` even-px mask
    // snapped scrolling to 2px). Render origin (simdraw.ts) and the gob cull
    // (below) drop the same mask in lockstep so the scroll blit stays aligned.
    const dx = c.PcFromWc(this.sim.m_wxView) - c.PcFromWc(wx);
    const dy = c.PcFromWc(this.sim.m_wyView) - c.PcFromWc(wy);
    this.host.Scroll(dx, dy); // gpfrmmSim->Scroll

    // Set the new view pos

    this.sim.SetViewPos(wx, wy);

    // Force redraw before next update

    this.evm.SetRedrawFlags(kfRedrawDirty | kfRedrawBeforeTimer);

    return true;
  }

  // game/Simulation.cpp:730-757 — reuse the gob list if it's the same update
  // count and view pos (ggobm.FindGobs == the ported renderFindGobs; the fog-map
  // arg is unused by the shipped FindGobs body)
  FindVisibleGobs(): Gob[] {
    if (
      this.sim.m_wxView !== this.m_wxViewSave ||
      this.sim.m_wyView !== this.m_wyViewSave ||
      this.sim.GetUpdateCount() !== this.m_cUpdatesSave
    ) {
      // Get visible gobs / refresh cache info

      const c = this.gctx.coords;
      const siz = this.GetPlayfieldSize();
      const xView = c.PcFromUwc(this.sim.m_wxView);
      const yView = c.PcFromUwc(this.sim.m_wyView);
      const rcVisible = {
        left: xView,
        top: yView,
        right: xView + siz.cx,
        bottom: yView + siz.cy,
      };
      this.m_apgobVisible = renderFindGobs(
        this.gctx,
        this.viewForFind,
        rcVisible,
        this.cpgobVisibleMax,
      ) as unknown as Gob[];
      this.m_wxViewSave = this.sim.m_wxView;
      this.m_wyViewSave = this.sim.m_wyView;
      this.m_cUpdatesSave = this.sim.GetUpdateCount();
    }
    return this.m_apgobVisible;
  }

  // game/Simulation.cpp:1048-1071 — passed coordinates are in world coordinates.
  // (The C++ Enum cursor collapses to first-match: every input-path caller takes
  // the first hit from a fresh Enum.)
  HitTest(wx: number, wy: number, wf: number): Gob | null {
    const apgob = this.FindVisibleGobs();
    for (let i = 0; i < apgob.length; i++) {
      const pgob = apgob[i];
      if ((pgob.GetFlags() & wf) !== wf) {
        continue;
      }

      const wrcT = new Rect();
      GobGetUIBounds(pgob, wrcT);
      if (!wrcT.PtIn(wx, wy)) {
        continue;
      }

      return pgob;
    }

    return null;
  }

  // game/Simulation.cpp:1075-1221 — passed coordinates are in world coordinates.
  // NOTE: the C++ leaves *pfHitSurrounding UNTOUCHED on the null-return paths
  // (the caller's stale value persists) — preserved.
  FingerHitTest(
    wx: number,
    wy: number,
    wf: number,
    pfHit: { fHitSurrounding: boolean },
  ): Gob | null {
    // Finger hit testing is tricky, because currently at least, a given tile is
    // smaller than a finger. [...] the gobs will be hit-tested specially, so
    // that close hits register, while taking special precaution for open
    // terrain between closely packed gobs.

    // Calc the world rect of the tile being hit

    const wrcTileHit = new Rect();
    wrcTileHit.left = WcTrunc(wx);
    wrcTileHit.top = WcTrunc(wy);
    wrcTileHit.right = wrcTileHit.left + kwcTile;
    wrcTileHit.bottom = wrcTileHit.top + kwcTile;
    const wrcTileHitAccum = new Rect();
    wrcTileHitAccum.SetEmpty();

    // Find the "closest" gob being hit, and keep track of hit area overlap
    // with the tile being hit.

    let pgobHitBest: Gob | null = null;
    let wcDistanceBest = kwcMax;

    const apgob = this.FindVisibleGobs();
    for (let i = 0; i < apgob.length; i++) {
      const pgobHit = apgob[i];
      if ((pgobHit.GetFlags() & wf) !== wf) {
        continue;
      }

      // Calc the inside rect - at least a tile's width and height
      // The inside rect is a direct hit on the gob. Return the
      // first direct hit, if there is one.

      const wrcInside = new Rect();
      GobGetUIBounds(pgobHit, wrcInside);
      let cwxInflate = 0;
      if (wrcInside.Width() < kwcTile) {
        cwxInflate += Math.trunc((kwcTile - wrcInside.Width()) / 2);
      }
      let cwyInflate = 0;
      if (wrcInside.Height() < kwcTile) {
        cwyInflate += Math.trunc((kwcTile - wrcInside.Height()) / 2);
      }
      wrcInside.Inflate(cwxInflate, cwyInflate);

      // Calc distance to this. If 0, we're inside and we're done
      // Remember "closest" gob.

      const wcDistance = wrcInside.GetDistance(wx, wy);
      if (wcDistance === 0) {
        pfHit.fHitSurrounding = false;
        return pgobHit;
      }
      if (wcDistance < wcDistanceBest) {
        wcDistanceBest = wcDistance;
        pgobHitBest = pgobHit;
      }

      // Calc the outside rect - accumulate overlap with the tile rect.

      const wrcOutside = new Rect();
      wrcOutside.SetRc(wrcInside);
      cwxInflate = 0;
      if (wrcOutside.Width() < kwcTile * 2) {
        cwxInflate += Math.trunc((kwcTile * 2 - wrcOutside.Width()) / 2);
      }
      cwyInflate = 0;
      if (wrcOutside.Height() < kwcTile * 2) {
        cwyInflate += Math.trunc((kwcTile * 2 - wrcOutside.Height()) / 2);
      }
      wrcOutside.Inflate(cwxInflate, cwyInflate);

      // Intersect and accumlate with tile rect.

      const wrcT = new Rect();
      if (wrcT.Intersect(wrcTileHit, wrcOutside)) {
        if (wrcTileHitAccum.IsEmpty()) {
          wrcTileHitAccum.SetRc(wrcT);
        } else {
          wrcTileHitAccum.Union(wrcT);
        }
      }
    }

    // If no gob or hit too far away, no hit.

    if (pgobHitBest === null) {
      return null;
    }

    // Structures get no slop since they are big already
    if (pgobHitBest.GetFlags() & kfGobStructure) {
      if (wcDistanceBest !== 0) {
        return null;
      }
    }

    // Check if this is a mobile headquarters that is moving. In this case,
    // selection is prioritized over local targetting, so that transforming
    // is easier. Local targetting is higher priority if the mobile HQ
    // is not moving.

    if (pgobHitBest.GetType() === kgtMobileHeadquarters) {
      const pmunt = pgobHitBest as MobileUnitGob;
      if (pmunt.IsMobile()) {
        if (wcDistanceBest <= kwcTile) {
          pfHit.fHitSurrounding = true;
          return pgobHitBest;
        }
      }
    }

    // See if the unit is close enough to qualify for being hit on.

    if (wcDistanceBest > kwcTileHalf) {
      return null;
    }

    // Check to see if the tile hit is completely obscured by overlapping
    // hit regions. If yes, then leave as open terrain (so that terrain can
    // be hit between closely packed gobs).

    if (wrcTileHitAccum.Equal(wrcTileHit)) {
      return null;
    }

    // Independent of hit thumbprint, if a gob is already selected, allow
    // the terrain next to it to be selected.

    if (pgobHitBest.GetFlags() & kfGobSelected) {
      return null;
    }

    // Structures are big enough to hit the regular bounds.
    if (pgobHitBest.GetFlags() & kfGobStructure) {
      return null;
    }

    // This gob has been hit, in the "surrounding" area.

    pfHit.fHitSurrounding = true;
    return pgobHitBest;
  }

  // game/simui.cpp:2040-2071
  HitTestGob(
    x: number,
    y: number,
    fFinger: boolean,
    pwpt: WPoint,
    pfHit: { fHitSurrounding: boolean },
  ): Gob | null {
    const c = this.gctx.coords;
    let wxTarget = this.sim.m_wxView + c.WcFromPc(x); // gsim.GetViewPos + WcFromPc
    let wyTarget = this.sim.m_wyView + c.WcFromPc(y);

    // Pin to edges. Gobs created off the map will cause corruption

    const dims = this.ctx.gobm.GetMapSize();
    if (TcFromWc(wxTarget) >= dims.ctx) {
      wxTarget = WcFromTc(dims.ctx - 1);
    }
    if (TcFromWc(wyTarget) >= dims.cty) {
      wyTarget = WcFromTc(dims.cty - 1);
    }

    pwpt.wx = wxTarget;
    pwpt.wy = wyTarget;

    if (fFinger) {
      return this.FingerHitTest(wxTarget, wyTarget, kfGobActive | kfGobUnit, pfHit);
    } else {
      const pgobHit = this.HitTest(wxTarget, wyTarget, kfGobActive | kfGobUnit);
      pfHit.fHitSurrounding = false;
      return pgobHit;
    }
  }

  // game/SimUI.cpp:2073-2080 — the by-pixel overload (the C++ overloads on
  // signature; only StylusHandler::OnPenEvent calls this one): an EXACT
  // (fFinger=false) hit test, then the gob-based MoveOrAttackOrSelect.
  MoveOrAttackOrSelectAt(x: number, y: number, ff: number): void {
    const wptTarget: WPoint = { wx: 0, wy: 0 };
    const hit = { fHitSurrounding: false };
    const pgobHit = this.HitTestGob(x, y, false, wptTarget, hit);
    this.MoveOrAttackOrSelect(pgobHit, wptTarget.wx, wptTarget.wy, ff);
  }

  // game/simui.cpp:1739-2028 — find targets for all selected MobileUnits: a
  // "closely packed" version of the current selection, centered around the
  // selection midpoint, placed at the target. ENDS in the sim's SetTarget — the
  // wire-exact kmidMove/kmidAttackCommand enqueue.
  SetSelectionTargets(
    gid: number,
    wxTarget: number,
    wyTarget: number,
    fAttackMove = false,
  ): MobileUnitGob | null {
    const ctx = this.ctx;

    // Find a target that is free and close

    const wptT0 = FindNearestFreeTile(ctx, TcFromWc(wxTarget), TcFromWc(wyTarget), 0);
    wxTarget = wptT0.wx;
    wyTarget = wptT0.wy;

    // First figure out which units are mobile and attempt to retain their formation

    let wxMin = kwcMax;
    let wxMax = 0;
    let wyMin = kwcMax;
    let wyMax = 0;

    let wcMoveDistPerUpdate = kwcMax; // (WCoord)kwcMax
    let cgobsSel = 0;
    let pgobT: Gob | null;
    for (pgobT = ctx.gobm.GetFirstGob(); pgobT !== null; pgobT = ctx.gobm.GetNextGob(pgobT)) {
      if (
        (pgobT.GetFlags() & (kfGobSelected | kfGobMobileUnit)) ===
          (kfGobSelected | kfGobMobileUnit) &&
        ((ctx.fGodMode && !ctx.fMultiplayer) || pgobT.GetSide() === ctx.plrLocal!.GetSide())
      ) {
        // Get selection dimensions

        const wpt: WPoint = { wx: 0, wy: 0 };
        pgobT.GetCenter(wpt);
        if (wpt.wx < wxMin) {
          wxMin = wpt.wx;
        }
        if (wpt.wx > wxMax) {
          wxMax = wpt.wx;
        }
        if (wpt.wy < wyMin) {
          wyMin = wpt.wy;
        }
        if (wpt.wy > wyMax) {
          wyMax = wpt.wy;
        }
        cgobsSel++;

        // Get min speed per update

        if (pgobT.GetFlags() & kfGobMobileUnit) {
          const pmuntc = (pgobT as MobileUnitGob).GetConsts() as MobileUnitConsts;
          if (pmuntc.GetMoveDistPerUpdate() < wcMoveDistPerUpdate) {
            wcMoveDistPerUpdate = pmuntc.GetMoveDistPerUpdate();
          }
        }
      }
    }
    let cwxSel = wxMax - wxMin;
    if (cwxSel <= 0) {
      cwxSel = 1;
    }
    let cwySel = wyMax - wyMin;
    if (cwySel <= 0) {
      cwySel = 1;
    }

    // The dest scaling rect is different if moving vs. attacking.

    const ctxSel = TcFromWc(wxMax) - TcFromWc(wxMin) + 1;
    const ctySel = TcFromWc(wyMax) - TcFromWc(wyMin) + 1;
    let ctxTarg: number;
    let ctyTarg: number;
    if (gid === kgidNull) {
      // Pick a destination scaling rect for movement

      ctxTarg = ctxSel;
      ctyTarg = ctySel;

      // Pack the rect if it is <= 50% full

      if (cgobsSel <= Math.trunc((ctxSel * ctySel + 1) / 2)) {
        let ctxT = ctxSel;
        let ctyT = ctySel;
        while (true) {
          // Remember last

          ctxTarg = ctxT;
          ctyTarg = ctyT;

          // Try smaller size, decrease whichever size is greater

          if (ctxT > ctyT) {
            ctxT--;
          } else {
            ctyT--;
          }

          // QUIRK: with an EMPTY mobile selection (every ground tap with nothing
          // selected), wxMin stays kwcMax so ctxSel/ctySel are NEGATIVE and the
          // product only goes < cgobsSel when the C++ int MULTIPLY OVERFLOWS
          // (ctxT,ctyT ~ -46341, ~92k iterations) — Math.imul reproduces the
          // int32 wrap the C++ terminates through; a plain JS multiply never
          // overflows and spins forever
          if (Math.imul(ctxT, ctyT) < cgobsSel + Math.trunc(cgobsSel / 3)) {
            break;
          }
        }
      }
    } else {
      // Pick a destination scaling rect for attacking

      const pgob = ctx.gobm.GetGob(gid);
      ctxTarg = 1;
      ctyTarg = 1;
      if (pgob !== null) {
        const trc = new TRect();
        pgob.GetTileRect(trc);
        ctxTarg = trc.right - trc.left;
        ctyTarg = trc.bottom - trc.top;
      }

      // Make the outside edge away from the gob; this hardwires some accounting
      // for some firing distance, gives a larger edge for gobs to move to

      ctxTarg += 2;
      ctyTarg += 2;
    }

    let cwxTarg = WcFromTc(ctxTarg - 1);
    if (cwxTarg === 0) {
      cwxTarg = 1;
    }
    let cwyTarg = WcFromTc(ctyTarg - 1);
    if (cwyTarg === 0) {
      cwyTarg = 1;
    }

    // Figure out the range of this target

    const tcTargetRadius = RadiusFromUnitCount(cgobsSel);

    // Find src center

    const wxCenterSrc = wxMin + Math.trunc((wxMax - wxMin) / 2);
    const wyCenterSrc = wyMin + Math.trunc((wyMax - wyMin) / 2);

    // Find map size (gsim.GetLevel()->GetTileMap()->GetTCoordMapSize — tile dims)

    const sizMapTiles = ctx.gobm.GetMapSize();
    const cwxMap = WcFromTc(sizMapTiles.ctx);
    const cwyMap = WcFromTc(sizMapTiles.cty);

    // Move & keep formation, or target destination if not a mobile unit

    const pgobTarget = ctx.gobm.GetGob(gid);

    let pmuntSetTarget: MobileUnitGob | null = null;
    for (pgobT = ctx.gobm.GetFirstGob(); pgobT !== null; pgobT = ctx.gobm.GetNextGob(pgobT)) {
      const ff = pgobT.GetFlags();
      if (
        ff & kfGobSelected &&
        ((ctx.fGodMode && !ctx.fMultiplayer) ||
          pgobT.GetSide() === ctx.plrLocal!.GetSide() ||
          (pgobTarget !== null && pgobTarget.GetType() === kgtReplicator))
      ) {
        // If it's a mobile gob, give it a move command to a scaled coordinate

        if (ff & kfGobMobileUnit) {
          // Calc dst

          const pmunt = pgobT as MobileUnitGob;
          const wptSrc: WPoint = { wx: 0, wy: 0 };
          pmunt.GetCenter(wptSrc);
          let wxDst: number;
          if (wptSrc.wx - wxCenterSrc >= 0) {
            wxDst = s16(
              Math.trunc(((wptSrc.wx - wxCenterSrc) * cwxTarg * 8 + cwxSel * 4) / (cwxSel * 8)) +
                wxTarget,
            );
          } else {
            wxDst = s16(
              Math.trunc(((wptSrc.wx - wxCenterSrc) * cwxTarg * 8 - cwxSel * 4) / (cwxSel * 8)) +
                wxTarget,
            );
          }
          if (wxDst < 0) {
            wxDst = 0;
          }
          if (wxDst >= cwxMap) {
            wxDst = cwxMap - 1;
          }
          let wyDst: number;
          if (wptSrc.wy - wyCenterSrc >= 0) {
            wyDst = s16(
              Math.trunc(((wptSrc.wy - wyCenterSrc) * cwyTarg * 8 + cwySel * 4) / (cwySel * 8)) +
                wyTarget,
            );
          } else {
            wyDst = s16(
              Math.trunc(((wptSrc.wy - wyCenterSrc) * cwyTarg * 8 - cwySel * 4) / (cwySel * 8)) +
                wyTarget,
            );
          }
          if (wyDst < 0) {
            wyDst = 0;
          }
          if (wyDst >= cwyMap) {
            wyDst = cwyMap - 1;
          }
          if (ctx.fGodMode && !ctx.fMultiplayer) {
            pmunt.ClearAction();
          }

          // Clean up dest if attacking

          if (pgobTarget !== null) {
            // Find the closest tile to this that is unblocked by terrain
            // Ok if on a structure... which structure is important, that is checked next

            const wptT = FindNearestFreeTile(ctx, TcFromWc(wxDst), TcFromWc(wyDst), 0);
            wxDst = wptT.wx;
            wyDst = wptT.wy;

            // Calc these now as wxDst, wyDst may have changed

            const txT = TcFromWc(wxDst);
            const tyT = TcFromWc(wyDst);

            // If dest is on top of a structure make sure that is the target and dest
            // is accessible

            // Assert(pgobTarget->GetFlags() & kfGobUnit);
            const puntTarget = pgobTarget as UnitGob;
            const pgobShadow = ctx.gobm.GetShadowGob(txT, tyT);
            if (pgobShadow !== null) {
              // The destination is on top of a structure. Need to
              // check if this is ok [...]

              // Assert(pgobShadow->GetFlags() & kfGobStructure);
              const pstruT = pgobShadow as StructGob;
              if (pgobShadow !== pgobTarget) {
                // Targetting something but wxDst, wyDst on a
                // structure we're not targetting. Get a valid
                // attack point from the real target

                const wptA: WPoint = { wx: 0, wy: 0 };
                puntTarget.GetAttackPoint(wptA);
                wxDst = wptA.wx;
                wyDst = wptA.wy;
              } else {
                // wxDst, wyDst is on top of the structure we are
                // targetting which is fine. Get an accessible
                // position if the one we have isn't accessible

                if (!pstruT.IsAccessible(txT, tyT)) {
                  const wptA: WPoint = { wx: 0, wy: 0 };
                  puntTarget.GetAttackPoint(wptA);
                  wxDst = wptA.wx;
                  wyDst = wptA.wy;
                }
              }
            }
          } else {
            // Not attacking, just moving.
            // Find a clear terrain spot that isn't terrain blocked and
            // not on a structure

            const wptT = FindNearestFreeTile(ctx, TcFromWc(wxDst), TcFromWc(wyDst), kbfStructure);
            wxDst = wptT.wx;
            wyDst = wptT.wy;
          }

          // Adjust the target radius so we end up on the same side of
          // any blockage

          let tcTargetRadiusT = tcTargetRadius;
          const ptrmap = ctx.trmap!; // gsim.GetLevel()->GetTerrainMap()
          const txTo = TcFromWc(wxTarget);
          const tyTo = TcFromWc(wyTarget);
          const txFrom = TcFromWc(wxDst);
          const tyFrom = TcFromWc(wyDst);
          const tptFree = ptrmap.FindLastUnoccupied(txTo, tyTo, txFrom, tyFrom);
          if (tptFree !== null) {
            if (tptFree.tx !== txFrom || tptFree.ty !== tyFrom) {
              wxDst = WcFromTc(tptFree.tx) + kwcTileHalf;
              wyDst = WcFromTc(tptFree.ty) + kwcTileHalf;
              const dtx = Math.abs(tptFree.tx - txTo);
              if (dtx < gmpDistFromDxy.length) {
                const dty = Math.abs(tptFree.ty - tyTo);
                if (dty < gmpDistFromDxy.length) {
                  tcTargetRadiusT = gmpDistFromDxy[dtx][dty];
                }
              }
            }
          }

          // Send command. An attack-move goes out as the attack-move command so the unit
          // engages enemies en route — to a ground point (gid null) or onto a target gob.
          if (fAttackMove) {
            pmunt.SetAttackMoveTarget(
              gid,
              wxDst,
              wyDst,
              wxTarget,
              wyTarget,
              tcTargetRadiusT,
              wcMoveDistPerUpdate,
            );
          } else {
            pmunt.SetTarget(
              gid,
              wxDst,
              wyDst,
              wxTarget,
              wyTarget,
              tcTargetRadiusT,
              wcMoveDistPerUpdate,
            );
          }
          pmuntSetTarget = pmunt;
        }

        // Else give it a set target command with the original target
        // coordinate
        else if (ff & kfGobUnit) {
          const punt = pgobT as UnitGob;
          // NOTE the C++ 5-arg call against the 7-param virtual (ht.h:6588):
          // wcMoveDistPerUpdate lands in the wyCenter SLOT; tcRadius and
          // wcMoveDistPerUpdate default to 0 — preserved verbatim
          punt.SetTarget(gid, wxTarget, wyTarget, 0, wcMoveDistPerUpdate, 0, 0);
        }
      }
    }

    return pmuntSetTarget;
  }

  // game/simui.cpp:2306-2446 — called on pen down to select the unit tapped on,
  // or on pen up to attack the unit tapped on / move to the location tapped on.
  MoveOrAttackOrSelect(pgobHit: Gob | null, wxTarget: number, wyTarget: number, ff: number): void {
    // TODO: break up this giant routine

    if (pgobHit === null) {
      // An attack-move ground target is treated like a move but issued as the attack-move
      // command + drawn red-tinted. Honored centrally for EVERY input path: the explicit
      // kfMasAttackMove bit (touch gestures) OR the armed flag (the HUD toggle, which the
      // mouse/StylusHandler path doesn't translate into a flag bit).
      const fAttackMove = (ff & kfMasAttackMove) !== 0 || this.m_fAttackMoveArmed;
      if (ff & (kfMasMove | kfMasAttackMove)) {
        const pmunt = this.SetSelectionTargets(kgidNull, wxTarget, wyTarget, fAttackMove);
        if (pmunt !== null) {
          // Single and multi-player friendly move target (simui.cpp:2338-2346;
          // the #if 0 SP CreateAnimGob arm is compiled out)

          if (this.m_nStateMoveTarget !== 0) {
            this.host.InvalidateTile(TcFromWc(this.m_wxMoveTarget), TcFromWc(this.m_wyMoveTarget));
          }
          this.host.StartMoveTargetAnim(); // m_aniMoveTarget.Start(0, 0, 0)
          this.m_wxMoveTarget = WcTrunc(wxTarget) + kwcTileHalf;
          this.m_wyMoveTarget = WcTrunc(wyTarget) + kwcTileHalf;
          this.m_nStateMoveTarget = 1;
          this.m_fMoveTargetAttack = fAttackMove; // red flourish for attack-move

          const muntc = this.ctx.unitConsts[pmunt.GetUnitType()] as unknown as MobileUnitConsts;
          // attack-move plays the attack voice (it's an offensive order); plain move the move voice
          this.host.PlaySfx(`sfxc:${fAttackMove ? muntc.sfxcAttack : muntc.sfxcMove}`);
        }

        // (RALLY_POINTS arm: not compiled in the shipped builds)
        this.m_fAttackMoveArmed = false; // an order was issued — consume the one-shot arming
      }
      return;
    }

    // Replicator is a special case. When it is specified as a target the
    // selected units are directed to its input — MobileUnitGob.SetTarget turns
    // this into a ground ATTACK-MOVE to the input point (TS-only), so the units
    // fight back en route instead of walking defenseless.

    if (pgobHit.GetType() === kgtReplicator) {
      if ((ff & (kfMasMove | kfMasAttack)) === 0) {
        return;
      }

      const pmunt = this.SetSelectionTargets(pgobHit.GetId(), wxTarget, wyTarget);
      if (pmunt !== null) {
        this.host.PlaySfx(
          `sfxc:${(this.ctx.unitConsts[pmunt.GetUnitType()] as unknown as MobileUnitConsts).sfxcMove}`,
        );
      }
      return;
    }

    // God mode is allowed only if gfGodMode is on when playing single player

    let fGodMode = false;
    if (this.ctx.fGodMode && !this.ctx.fMultiplayer) {
      fGodMode = true;
    }
    void fGodMode; // (computed but unused in the shipped routine)

    // Selection?

    if (this.IsSelectionCommand(pgobHit)) {
      if (ff & kfMasSelect) {
        const fAlreadySelected = (pgobHit.GetFlags() & kfGobSelected) !== 0;

        // Selection. Cancel selection for units elsewhere, set selection
        // for this unit

        // If the unit is already selected and the last selection occured
        // in within the double-tap window then select all units of the
        // same type on-screen.	 UNDONE: this may not be the best place to
        // do this.

        if (
          fAlreadySelected &&
          this.GetTickCount() - this.s_tLastSelected < kctDoubleTapDelay &&
          this.s_pgobLastSelected === pgobHit
        ) {
          this.SelectSameUnitTypes(pgobHit, false);
        } else {
          SimulationSetGobSelected(this.ctx, pgobHit); // gsim.SetGobSelected
        }
        this.s_tLastSelected = this.GetTickCount();
        this.s_pgobLastSelected = pgobHit;

        // Play sfx

        if (!this.ctx.fGodMode) {
          if (pgobHit.GetFlags() & kfGobStructure) {
            this.host.PlaySfx(
              (this.ctx.unitConsts[(pgobHit as StructGob).GetUnitType()] as unknown as StructConsts)
                .sfxSelect,
            );
          } else {
            if (this.ctx.fGodMode || pgobHit.GetSide() === this.ctx.plrLocal!.GetSide()) {
              this.host.PlaySfx(
                `sfxc:${(this.ctx.unitConsts[(pgobHit as UnitGob).GetUnitType()] as unknown as MobileUnitConsts).sfxcSelect}`,
              );
            }
          }
        }
      }

      if (ff & kfMasShowMenu) {
        if (pgobHit.GetFlags() & kfGobStructure) {
          this.ShowUnitMenu(pgobHit);
        }
      }
    } else if (ff & kfMasAttack) {
      // An attack. Reset the wxTarget, wyTarget to be the middle of the gob
      // and issue targets. With kfMasAttackMove (a tap-hold / armed HUD on the target) it's an
      // attack-move-to-target: the units fight through en-route enemies, then finish on it.

      const fAttackMove = (ff & kfMasAttackMove) !== 0 || this.m_fAttackMoveArmed;
      const puntHit = pgobHit as UnitGob;
      const wptTarget: WPoint = { wx: 0, wy: 0 };
      puntHit.GetAttackPoint(wptTarget);
      wxTarget = wptTarget.wx;
      wyTarget = wptTarget.wy;
      const pmunt = this.SetSelectionTargets(puntHit.GetId(), wxTarget, wyTarget, fAttackMove);
      if (pmunt !== null) {
        this.host.PlaySfx(
          `sfxc:${(this.ctx.unitConsts[pmunt.GetUnitType()] as unknown as MobileUnitConsts).sfxcAttack}`,
        );
        // A targeted attack-move does NOT drop a ground reticle — the target itself flashes
        // RED (SetAttackMoveTarget -> FlashAttackMove), the same way a normal attack flashes it.
      }
      // an attack order was issued — consume the one-shot arming (selections do not).
      this.m_fAttackMoveArmed = false;
    }
  }

  // game/simui.cpp:2448-2491 — figure out if this is a selection or an attack
  // command (player can select same side units; enemy units only if no friendly
  // units are selected; attack otherwise; god mode variations).
  IsSelectionCommand(pgobHit: Gob): boolean {
    // God mode is allowed only if gfGodMode is on when playing single player

    let fGodMode = false;
    if (this.ctx.fGodMode && !this.ctx.fMultiplayer) {
      fGodMode = true;
    }

    // If there are any selected units that can attack pgobHit, it may be an
    // attack.

    let fOwnSelection = false;
    let fSelection = true;
    // (Side sideHit = pgobHit->GetSide() — computed but unused in the shipped code)
    for (
      let pgobT = this.ctx.gobm.GetFirstGob();
      pgobT !== null;
      pgobT = this.ctx.gobm.GetNextGob(pgobT)
    ) {
      if ((pgobT.GetFlags() & (kfGobUnit | kfGobSelected)) !== (kfGobUnit | kfGobSelected)) {
        continue;
      }
      const puntT = pgobT as UnitGob;
      if (puntT.GetOwner() === this.ctx.plrLocal) {
        fOwnSelection = true;
      }
      if (puntT.IsValidTarget(pgobHit)) {
        fSelection = false;
        break;
      }
    }

    // If not in god mode and the local player does not own any of the already
    // selected units then just select the new unit

    if (!fGodMode && !fOwnSelection) {
      fSelection = true;
    }

    return fSelection;
  }

  // game/simui.cpp:2493-2506
  HasSelectedUnits(): boolean {
    let pgobT = this.ctx.gobm.GetFirstGob();
    for (; pgobT !== null; pgobT = this.ctx.gobm.GetNextGob(pgobT)) {
      if (
        (pgobT.GetFlags() & (kfGobUnit | kfGobSelected | kfGobStructure)) !==
        (kfGobUnit | kfGobSelected)
      ) {
        continue;
      }
      if ((pgobT as UnitGob).GetOwner() === this.ctx.plrLocal) {
        return true;
      }
    }
    return false;
  }

  // TS-only attack-move arming, driven by the HUD toggle button (and consumed by the next
  // ground tap, which disarms it). The tap-hold gesture sets m_fAttackMoveArmed directly.
  /** Flip the armed state; returns the new value (for the HUD button to reflect). */
  ToggleAttackMoveArmed(): boolean {
    this.m_fAttackMoveArmed = !this.m_fAttackMoveArmed;
    return this.m_fAttackMoveArmed;
  }
  SetAttackMoveArmed(f: boolean): void {
    this.m_fAttackMoveArmed = f;
  }
  IsAttackMoveArmed(): boolean {
    return this.m_fAttackMoveArmed;
  }

  // game/simui.cpp:2508-2519
  ShowUnitMenu(pgob: Gob | null): void {
    if (pgob !== null && (pgob.GetFlags() & kfGobUnit) !== 0) {
      // Don't popup menu if the player doesn't own the Gob (unless in
      // God mode). God mode is limited for multiplayer games.
      if (
        (!this.ctx.fGodMode || this.ctx.fMultiplayer) &&
        pgob.GetSide() !== this.ctx.plrLocal!.GetSide()
      ) {
        return;
      }
      this.host.PopupMenu(pgob); // pgob->PopupMenu()
    }
  }

  // game/simui.cpp:2521-2546 — QUIRK: the fSfx parameter is accepted but never
  // tested; the sfx plays whenever the side gate passes (verbatim).
  SelectSameUnitTypes(pgob: Gob, fSfx: boolean): void {
    void fSfx;
    if ((pgob.GetFlags() & (kfGobUnit | kfGobStructure)) !== kfGobUnit) {
      return;
    }

    const c = this.gctx.coords;
    const wxViewT = this.sim.m_wxView; // gsim.GetViewPos
    const wyViewT = this.sim.m_wyView;
    const txView = TcFromWc(wxViewT);
    const tyView = TcFromWc(wyViewT);

    const sizT = this.GetPlayfieldSize(); // ggame.GetPlayfieldSize
    const txRight = TcFromWc(wxViewT + c.WcFromPc(sizT.cx + c.gcxTile - 1));
    const tyBottom = TcFromWc(wyViewT + c.WcFromPc(sizT.cy + c.gcyTile - 1));

    const trc = new TRect();
    trc.Set(txView, tyView, txRight, tyBottom);
    SimulationSelectSameUnitTypes(this.ctx, pgob as UnitGob, trc); // gsim.SelectSameUnitTypes

    if (this.ctx.fGodMode || pgob.GetSide() === this.ctx.plrLocal!.GetSide()) {
      this.host.PlaySfx(
        `sfxc:${(this.ctx.unitConsts[(pgob as UnitGob).GetUnitType()] as unknown as MobileUnitConsts).sfxcSelect}`,
      );
    }
  }
}

// The WI playfield finger UI:
//
// 1. One finger for selecting and rubber banding around troops
// 2. Two fingers for scrolling the map.
// 3. Two finger flick for momentum scrolls.
//
// Notes:
// - During a select operation, the second finger may come down. This cancels
//	 the selection and enters scroll mode. Units that were selected as part of
//	 entering the mode are unselected when entering scroll mode. Units that
//	 were selected before select mode was entered are left selected.
//
// - Once entered, scroll mode remains even if one of the fingers goes up
//
// - At the beginning of scroll mode, the first down is the reference
//	 point.
//
// - If in scroll mode, and one of the fingers goes up while the other remains
//	 down, the aggregate is still down, and the coordinate is based on the
//	 reference point so there is no map jumping.

// ht.h:3749-3757 (SimUIForm::PenHandler) — the m_ppenh virtual surface both
// handlers implement (kuitFinger = FingerHandler, kuitStylus = StylusHandler;
// Dispose mirrors the C++ virtual dtor SetUIType's `delete m_ppenh` reaches).
export interface PenHandler {
  OnPenEvent(pevt: Event, fScrollOnly: boolean): boolean;
  OnPaint(pbm: unknown): void;
  CheckScroll(n?: number): void;
  GetFlags(): number;
  SetFlags(ff: number): void;
  Dispose(): void;
}

// ht.h:3815-3881 (SimUIForm::FingerHandler) + game/fingerhandler.cpp
export class FingerHandler {
  private m_psui: SimUIForm;
  private m_xDownLast = 0;
  private m_yDownLast = 0;
  private m_wxTarget = 0;
  private m_wyTarget = 0;
  private m_fHitSurrounding = false;
  private m_gidHitLast: number = kgidNull;
  private m_gidHilight: number = kgidNull;
  // (ht.h:3856-3859 m_tDoubleTap/m_xDoubleTapDownLast/m_yDoubleTapDownLast/
  // m_fTimerSet: declared but never touched by fingerhandler.cpp — omitted)
  private m_fShowUnitMenu = false;
  private m_pfrmUnitTitle: unknown = null;
  private m_ff = 0;
  private m_pselspr: BaseSelectionSprite | null;
  private m_maskA = 0;
  private m_maskB = 0;
  private m_x1 = 0;
  private m_y1 = 0;
  private m_x2 = 0;
  private m_y2 = 0;
  private m_vOffsetA = new Vec2d();
  private m_vOffsetB = new Vec2d();
  private m_xDrag = 0;
  private m_yDrag = 0;
  private m_wxViewDrag = 0;
  private m_wyViewDrag = 0;
  private m_tDown = 0;
  private m_state = FHS_NONE;

  // game/fingerhandler.cpp:33-54
  constructor(psui: SimUIForm) {
    this.m_psui = psui;
    this.m_gidHitLast = kgidNull;
    this.m_gidHilight = kgidNull;
    this.m_xDownLast = 0;
    this.m_yDownLast = 0;
    this.m_state = FHS_NONE;
    this.m_fShowUnitMenu = false;
    this.m_pfrmUnitTitle = null;
    this.m_ff = 0;

    // Tell the placement form to pass input through, which will
    // allow the this handler to scroll the playfield.
    const gpfrmPlace = psui.host.GetPlaceForm();
    if (gpfrmPlace !== null) {
      gpfrmPlace.SetPassOnInput(true);
    }

    this.m_pselspr = psui.host.CreateSelectionSprite(); // gpsprm->CreateSelectionSprite()
    if (this.m_pselspr !== null) {
      this.m_pselspr.Show(false);
    }
  }

  // game/fingerhandler.cpp:56-59 (~FingerHandler)
  Dispose(): void {
    if (this.m_pfrmUnitTitle !== null) {
      // delete m_pfrmUnitTitle
      this.m_psui.host.DestroyUnitTitle(this.m_pfrmUnitTitle);
    }
    this.m_pfrmUnitTitle = null;
    this.m_pselspr = null; // delete m_pselspr
  }

  // ht.h:3823-3824 (PenHandler virtuals)
  GetFlags(): number {
    return this.m_ff;
  }
  SetFlags(ff: number): void {
    this.m_ff = ff;
  }

  // game/fingerhandler.cpp:61-127 (the #if 0 IPhone::Log block is compiled out)
  OnPenEvent(pevt: Event, fScrollOnly: boolean): boolean {
    switch (pevt.eType) {
      case penDownEvent:
        this.OnPenDown(pevt, fScrollOnly);
        break;

      case penDownEvent2:
        this.OnPenDown2(pevt, fScrollOnly);
        break;

      case penUpEvent:
        this.OnPenUp(pevt, fScrollOnly);
        break;

      case penUpEvent2:
        this.OnPenUp2(pevt, fScrollOnly);
        break;

      case penMoveEvent:
        this.OnPenMove(pevt);
        break;

      case penMoveEvent2:
        this.OnPenMove2(pevt);
        break;

      case penHoldEvent:
        this.OnPenHold(pevt);
        break;

      default:
        return false;
    }
    return true;
  }

  // game/fingerhandler.cpp:129-176
  private OnPenDown(pevt: Event, fScrollOnly: boolean): void {
    this.m_tDown = this.m_psui.GetTickCount(); // base::GetTickCount()
    this.m_xDownLast = pevt.x;
    this.m_yDownLast = pevt.y;
    this.m_x1 = pevt.x;
    this.m_y1 = pevt.y;
    this.m_ff |= kfPhFinger1Down;

    // The first finger can come down while in FHS_SELECT mode
    if (this.m_state === FHS_SELECT) {
      this.UpdateSelect(pevt);
      return;
    }

    // Not in select mode, then there should be no modes operating
    const wptTarget: WPoint = { wx: 0, wy: 0 };
    const hit = { fHitSurrounding: this.m_fHitSurrounding };
    let pgob = this.m_psui.HitTestGob(pevt.x, pevt.y, true, wptTarget, hit);
    this.m_wxTarget = wptTarget.wx;
    this.m_wyTarget = wptTarget.wy;
    this.m_fHitSurrounding = hit.fHitSurrounding;

    // Pretend no gob hittesting if it is in fog
    if (pgob !== null) {
      const trc = new TRect();
      pgob.GetTileRect(trc);
      if (this.m_psui.ctx.fogm!.IsCovered(trc)) {
        // gsim.GetLevel()->GetFogMap()
        pgob = null;
      }
    }
    this.m_gidHitLast = pgob === null ? kgidNull : pgob.GetId();

    this.EnterNone();

    if (fScrollOnly) {
      this.EnterDrag(pevt);
      return;
    }

    if (this.CheckSelect(pevt)) {
      this.EnterSelect(pevt);
      return;
    }

    if (pgob !== null) {
      this.EnterHilight(pgob);
      return;
    }

    // Enter drag right away to make the UI responsive
    this.EnterDrag(pevt);
  }

  // game/fingerhandler.cpp:178-199
  private OnPenDown2(pevt: Event, fScrollOnly: boolean): void {
    this.m_x2 = pevt.x;
    this.m_y2 = pevt.y;
    this.m_ff |= kfPhFinger2Down;

    // fScrollOnly is set if in observe mode, or in structure placement mode
    if (fScrollOnly) {
      return;
    }

    // May already be in FHS_SELECT when finger 2 comes down
    if (this.m_state === FHS_SELECT) {
      this.UpdateSelect(pevt);
      return;
    }

    // Otherwise, enter it
    if (this.CheckSelect(pevt)) {
      this.EnterSelect(pevt);
      return;
    }
  }

  // game/fingerhandler.cpp:201-220
  private OnPenMove(pevt: Event): void {
    this.m_x1 = pevt.x;
    this.m_y1 = pevt.y;

    if (this.m_state === FHS_DRAG) {
      this.UpdateDrag(pevt);
      return;
    }

    if (this.m_state === FHS_SELECT) {
      this.UpdateSelect(pevt);
      return;
    }

    if (this.m_state === FHS_HILIGHT) {
      if (this.CheckDragged(pevt, LARGE_DRAG)) {
        this.EnterDrag(pevt);
      }
    }
  }

  // game/fingerhandler.cpp:222-230
  private OnPenMove2(pevt: Event): void {
    this.m_x2 = pevt.x;
    this.m_y2 = pevt.y;

    if (this.m_state === FHS_SELECT) {
      this.UpdateSelect(pevt);
      return;
    }
  }

  // game/fingerhandler.cpp:232-335
  private OnPenUp(pevt: Event, fScrollOnly: boolean): void {
    this.m_x1 = pevt.x;
    this.m_y1 = pevt.y;
    this.m_ff &= ~kfPhFinger1Down;

    // The form manager sometimes posts these when modes need to be
    // gracefully broken out of when a new form shows during capture.

    if (pevt.dw === 1) {
      this.m_ff &= ~(kfPhFinger1Down | kfPhFinger2Down);
      this.EnterNone();
      return;
    }

    let fTarget = false;
    switch (this.m_state) {
      case FHS_DRAG:
        // Try to detect a quick targetting jab and don't scroll the map
        if (this.IsQuickUp(pevt)) {
          fTarget = !this.CheckDragged(pevt, MEDIUM_DRAG);
        } else {
          fTarget = !this.CheckDragged(pevt, SMALL_DRAG);
        }
        if (fTarget) {
          break;
        }
      // fall through
      default:
        // Update the position, since there may be a new position
        this.OnPenMove(pevt);
    }

    if (this.m_state === FHS_NONE) {
      return;
    }

    if (this.m_state === FHS_SELECT) {
      if (this.m_ff & kfPhFinger2Down) {
        this.UpdateSelect(pevt);
      } else {
        this.EnterNone();
      }
      return;
    }

    if (this.m_state === FHS_DRAG) {
      const gpfrmPlace = this.m_psui.host.GetPlaceForm();
      if (gpfrmPlace !== null && gpfrmPlace.IsBusy()) {
        if (!this.CheckDragged(pevt, LARGE_DRAG)) {
          gpfrmPlace.UpdatePosition(pevt);
        }
        this.EnterNone();
        return;
      }
      if (fTarget && !fScrollOnly) {
        // Armed attack-move (HUD toggle or a tap-hold over empty ground) turns this ground
        // tap into an attack-move; otherwise it's a plain move.
        const fas = this.m_psui.m_fAttackMoveArmed ? kfMasAttackMove : kfMasMove;
        this.m_psui.MoveOrAttackOrSelect(null, this.m_wxTarget, this.m_wyTarget, fas);
      }
      this.EnterNone();
      return;
    }

    // Select or attack hilighted gob.
    if (this.m_state === FHS_HILIGHT) {
      if (this.m_gidHilight === kgidNull) {
        this.EnterNone();
        return;
      }

      // EnterNone before showing the menu since that is modal
      const pgobT = this.m_psui.ctx.gobm.GetGob(this.m_gidHilight);
      let fShowUnitMenu = this.m_fShowUnitMenu;
      this.EnterNone();

      if (pgobT === null || (pgobT.GetFlags() & kfGobUnit) === 0) {
        return;
      }

      // Structures always get their menu shown when selected this way.
      if (pgobT.GetFlags() & kfGobStructure) {
        fShowUnitMenu = true;
      }

      // If showing the unit menu, make sure the command is selection.
      // When a miner targets a friendly processor, it isn't selection for
      // example.
      if (fShowUnitMenu && !this.m_psui.IsSelectionCommand(pgobT)) {
        fShowUnitMenu = false;
      }

      // Select or attack. When attack-move is armed (a tap-hold on the target, or the HUD
      // toggle), an attack on an enemy becomes an attack-move-to-target.
      const fas = this.m_psui.m_fAttackMoveArmed ? kfMasAttackMove : 0;
      this.m_psui.MoveOrAttackOrSelect(
        pgobT,
        this.m_wxTarget,
        this.m_wyTarget,
        kfMasSelect | kfMasAttack | fas,
      );

      // Show the menu last since it is modal
      if (fShowUnitMenu) {
        this.m_psui.ShowUnitMenu(pgobT);
      }
      return;
    }

    // What is this state?
    // Assert();
    this.EnterNone();
  }

  // game/fingerhandler.cpp:337-350
  private OnPenUp2(pevt: Event, _fScrollOnly: boolean): void {
    this.m_ff &= ~kfPhFinger2Down;
    this.m_x2 = pevt.x;
    this.m_y2 = pevt.y;

    if (this.m_state === FHS_SELECT) {
      if (this.m_ff & kfPhFinger1Down) {
        this.UpdateSelect(pevt);
      } else {
        this.EnterNone();
      }
      return;
    }
  }

  // game/fingerhandler.cpp:352-383
  private OnPenHold(pevt: Event): void {
    // TS-only gesture: a stationary tap-hold over EMPTY ground (the drag never moved, so the
    // hold timer fired) ARMS attack-move when own units are selected — the pen-up then issues
    // the attack-move at this point. m_gidHitLast is null over empty ground (set in OnPenDown).
    if (
      this.m_state === FHS_DRAG &&
      this.m_gidHitLast === kgidNull &&
      this.m_psui.HasSelectedUnits()
    ) {
      this.m_psui.m_fAttackMoveArmed = true;
      return;
    }

    if (this.m_state === FHS_HILIGHT) {
      if (this.m_gidHilight === kgidNull) {
        return;
      }

      // If actually hit on the surrounding gob hit test area,
      // and not the gob itself, switch to drag. This allows for
      // precise move targeting around gobs. This is only useful
      // if there are friendly units selected.
      if (this.m_fHitSurrounding && this.m_psui.HasSelectedUnits()) {
        // Don't enter drag for the mobile HQ, so that transforming
        // it is easier. This trades of nearby targetting with easy
        // of transforming.
        const pgob = this.m_psui.ctx.gobm.GetGob(this.m_gidHilight);
        if (pgob === null || pgob.GetType() !== kgtMobileHeadquarters) {
          this.EnterDrag(pevt);
          return;
        }
      }

      // TS-only gesture: a tap-hold directly on an ENEMY (an attack target for the current
      // selection) ARMS attack-move-to-target — the pen-up issues an attack-move ONTO it
      // (fight through en-route enemies, finish on the target) instead of a plain attack.
      const pgobHold = this.m_psui.ctx.gobm.GetGob(this.m_gidHilight);
      if (
        pgobHold !== null &&
        this.m_psui.HasSelectedUnits() &&
        !this.m_psui.IsSelectionCommand(pgobHold)
      ) {
        this.m_psui.m_fAttackMoveArmed = true;
        this.ShowUnitTitle(pgobHold); // hold-long-enough confirmation
        return;
      }

      // Hit directly on gob. Don't change modes. This is simple
      // to understand for the user. When then pen goes up,
      // show the unit menu.
      this.m_fShowUnitMenu = true;

      // In the meantime, show the unit title. This gives confirmation
      // to the user that they've held the pen long enough.
      this.ShowUnitTitle(this.m_psui.ctx.gobm.GetGob(this.m_gidHilight));
      return;
    }
  }

  // game/fingerhandler.cpp:385-393
  private UnhilightGob(): void {
    if (this.m_gidHilight !== kgidNull) {
      const pgobT = this.m_psui.ctx.gobm.GetGob(this.m_gidHilight);
      if (pgobT !== null && pgobT.GetFlags() & kfGobUnit) {
        UnitGobHilight(pgobT as UnitGob, false, this.m_psui.host); // ->Hilight(false)
      }
      this.m_gidHilight = kgidNull;
    }
  }

  // game/fingerhandler.cpp:395-402
  private EnterNone(): void {
    this.m_pselspr!.Show(false);
    this.UnhilightGob();
    this.m_fShowUnitMenu = false;
    if (this.m_pfrmUnitTitle !== null) {
      // delete m_pfrmUnitTitle
      this.m_psui.host.DestroyUnitTitle(this.m_pfrmUnitTitle);
    }
    this.m_pfrmUnitTitle = null;
    this.m_state = FHS_NONE;
  }

  // game/fingerhandler.cpp:404-409
  private EnterHilight(pgob: Gob): void {
    // Assert((pgob->GetFlags() & kfGobUnit) != 0);
    UnitGobHilight(pgob as UnitGob, true, this.m_psui.host); // ->Hilight(true)
    this.m_gidHilight = pgob.GetId();
    this.m_state = FHS_HILIGHT;
  }

  // game/fingerhandler.cpp:411-414
  private CheckSelect(_pevt: Event): boolean {
    return (
      (this.m_ff & (kfPhFinger1Down | kfPhFinger2Down)) === (kfPhFinger1Down | kfPhFinger2Down)
    );
  }

  // game/fingerhandler.cpp:416-484
  private EnterSelect(_pevt: Event): void {
    // Leave current modes
    this.EnterNone();

    // Form a box around the two down points
    const rc = new Rect();
    if (this.m_x1 < this.m_x2) {
      rc.left = this.m_x1;
      rc.right = this.m_x2;
    } else {
      rc.left = this.m_x2;
      rc.right = this.m_x1;
    }
    if (this.m_y1 < this.m_y2) {
      rc.top = this.m_y1;
      rc.bottom = this.m_y2;
    } else {
      rc.top = this.m_y2;
      rc.bottom = this.m_y1;
    }

    // Initialize the drag rect. It expects a bottom left coordinate
    // system. If the rect is rotated 90 cw, then it fits SimUI's
    // coordinate system if x/y are swapped.

    const pt0: DPoint = { x: rc.top, y: rc.left };
    const pt1: DPoint = { x: rc.bottom, y: rc.left };
    const pt2: DPoint = { x: rc.bottom, y: rc.right };
    const drc = new DragRect();
    drc.Init(pt0, pt1, pt2);

    // Figure out the tracking masks

    const ptA: DPoint = { x: this.m_y1, y: this.m_x1 };
    this.m_maskA = drc.HitTest(ptA, this.m_vOffsetA);
    if (this.m_vOffsetA.mag() > kcpCloseEnough) {
      this.m_vOffsetA = new Vec2d(0, 0);
    }

    const ptB: DPoint = { x: this.m_y2, y: this.m_x2 };
    this.m_maskB = drc.HitTest(ptB, this.m_vOffsetB);
    if (this.m_vOffsetB.mag() > kcpCloseEnough) {
      this.m_vOffsetB = new Vec2d(0, 0);
    }

    // Show the drag rect

    this.m_pselspr!.SetDragRect(drc);
    this.m_pselspr!.Show(true);

    // Redraw the screen so the selection updates
    this.m_psui.evm.SetRedrawFlags(kfRedrawDirty | kfRedrawBeforeTimer);

    // Set selection based on this rect
    this.SetSelection();

    // Enter FHS_SELECT mode
    this.m_state = FHS_SELECT;
  }

  // game/fingerhandler.cpp:486-525
  private UpdateSelect(pevt: Event): void {
    const ptA: DPoint = { x: this.m_y1, y: this.m_x1 };
    const ptB: DPoint = { x: this.m_y2, y: this.m_x2 };

    // Update tracking masks
    const drc = this.m_pselspr!.GetDragRect().Clone(); // DragRect drc = ... (value copy)
    switch (pevt.eType) {
      case penDownEvent:
        this.m_maskA = drc.HitTest(ptA, this.m_vOffsetA);
        if (this.m_vOffsetA.mag() > kcpCloseEnough) {
          this.m_vOffsetA = new Vec2d(0, 0);
        }
        break;

      case penDownEvent2:
        this.m_maskB = drc.HitTest(ptB, this.m_vOffsetB);
        if (this.m_vOffsetB.mag() > kcpCloseEnough) {
          this.m_vOffsetB = new Vec2d(0, 0);
        }
        break;

      case penUpEvent:
        this.m_maskA = 0;
        break;

      case penUpEvent2:
        this.m_maskB = 0;
        break;
    }

    drc.TrackPoints(this.m_maskA, this.m_vOffsetA.add(ptA), this.m_maskB, this.m_vOffsetB.add(ptB));
    this.m_pselspr!.SetDragRect(drc);
    this.SetSelection();

    // Redraw the screen so the selection updates
    this.m_psui.evm.SetRedrawFlags(kfRedrawDirty | kfRedrawBeforeTimer);
  }

  // game/fingerhandler.cpp:527-632 — n = ticks since the last poll (polled per
  // tick now; the step scales by n to keep a smooth, constant edge-scroll speed)
  CheckScroll(n = 1): void {
    // Don't auto-scroll if not drag selecting
    if (!this.m_pselspr!.IsVisible()) {
      return;
    }
    const wcStep = Math.max(1, kwcScrollStepPerTick * n);

    // Beta feedback: users don't like auto scrolling.
    // Don't auto scroll if both fingers are down. Do auto scroll if
    // one finger is extending a rect - this keeps the old behavior.

    if (this.m_maskA !== 0 && this.m_maskB !== 0) {
      return;
    }

    // First see if either finger is in the "scroll border" area

    const c = this.m_psui.gctx.coords;
    const sizPlayfield = this.m_psui.GetPlayfieldSize(); // ggame.GetPlayfieldSize
    const cpBorder = c.PcFromUwc(kwcScrollBorderSize);
    let wfAdjust = 0;

    // The portrait mode status bar on the iPhone isn't visible when
    // running the game, but it still eats input! Scroll before hitting
    // that.

    const cpLeftExtra = c.PcFromUwc(kwcScrollLeftExtra);

    // For some reason, the iphone passes a finger up before the finger
    // gets close enough to the right edge of the screen to scroll within
    // cpBorder. So, subtract extra from the right side.

    const cpRightExtra = cpLeftExtra;

    if (this.m_maskA !== 0) {
      if (this.m_x1 < cpBorder + cpLeftExtra) {
        wfAdjust |= 1;
      } else if (this.m_x1 > sizPlayfield.cx - cpBorder - cpRightExtra) {
        wfAdjust |= 4;
      }
      if (this.m_y1 < cpBorder) {
        wfAdjust |= 2;
      } else if (this.m_y1 > sizPlayfield.cy - cpBorder) {
        wfAdjust |= 8;
      }
    }

    if (this.m_maskB !== 0) {
      if (this.m_x2 < cpBorder + cpLeftExtra) {
        wfAdjust |= 1;
      } else if (this.m_x2 > sizPlayfield.cx - cpBorder - cpRightExtra) {
        wfAdjust |= 4;
      }
      if (this.m_y2 < cpBorder) {
        wfAdjust |= 2;
      } else if (this.m_y2 > sizPlayfield.cy - cpBorder) {
        wfAdjust |= 8;
      }
    }
    if (wfAdjust === 0) {
      return;
    }

    // Set the new view pos

    const wptView: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wptView);
    const wxView = wptView.wx;
    const wyView = wptView.wy;
    let wxViewNew = wxView;
    let wyViewNew = wyView;
    if (wfAdjust & 1) {
      wxViewNew -= wcStep;
    }
    if (wfAdjust & 4) {
      wxViewNew += wcStep;
    }
    if (wfAdjust & 2) {
      wyViewNew -= wcStep;
    }
    if (wfAdjust & 8) {
      wyViewNew += wcStep;
    }
    this.m_psui.SetViewPos(wxViewNew, wyViewNew);

    // If tracking two fingers, the rect stays where it is. It
    // only needs to update the selection.

    if (this.m_maskA !== 0 && this.m_maskB !== 0) {
      this.SetSelection();
      this.m_psui.evm.SetRedrawFlags(kfRedrawDirty | kfRedrawBeforeTimer);
      return;
    }

    // Otherwise tracking one finger; expand the size of the rect.
    // Note the coordinates passed to scroll are rotated since that
    // is how the drag rect is being maintained.

    const wptActual: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wptActual);
    const dx = c.PcFromWc(wxView - wptActual.wx);
    const dy = c.PcFromWc(wyView - wptActual.wy);
    const drc = this.m_pselspr!.GetDragRect().Clone();
    drc.ScrollExpand(this.m_maskA, this.m_maskB, dy, dx);
    this.m_pselspr!.SetDragRect(drc);
    this.SetSelection();
    this.m_psui.evm.SetRedrawFlags(kfRedrawDirty | kfRedrawBeforeTimer);
  }

  // game/fingerhandler.cpp:634-639
  private IsQuickUp(pevt: Event): boolean {
    if (pevt.eType !== penUpEvent) {
      return false;
    }
    return this.m_psui.GetTickCount() - this.m_tDown <= QUICK_UP_TICKS;
  }

  // game/fingerhandler.cpp:641-648
  private CheckDragged(pevt: Event, wcDrag: number): boolean {
    const pcDrag = this.m_psui.gctx.coords.PcFromWc(wcDrag);
    if (
      Math.abs(pevt.x - this.m_xDownLast) > pcDrag ||
      Math.abs(pevt.y - this.m_yDownLast) > pcDrag
    ) {
      return true;
    }
    return false;
  }

  // game/fingerhandler.cpp:650-656
  private EnterDrag(pevt: Event): void {
    this.EnterNone();
    this.m_xDrag = pevt.x;
    this.m_yDrag = pevt.y;
    const wpt: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wpt); // gsim.GetViewPos
    this.m_wxViewDrag = wpt.wx;
    this.m_wyViewDrag = wpt.wy;
    this.m_state = FHS_DRAG;
  }

  // game/fingerhandler.cpp:658-663
  private UpdateDrag(pevt: Event): void {
    let v = new Vec2d(this.m_xDrag - pevt.x, this.m_yDrag - pevt.y);
    v = v.scale(this.m_psui.gnScrollSpeed);
    const c = this.m_psui.gctx.coords;
    // WcFromPc takes an int pc — the scaled double TRUNCATES at the call (C++
    // implicit double->int conversion)
    this.m_psui.SetViewPos(
      this.m_wxViewDrag + c.WcFromPc(Math.trunc(v.dx)),
      this.m_wyViewDrag + c.WcFromPc(Math.trunc(v.dy)),
    );
  }

  // game/fingerhandler.cpp:665-666
  OnPaint(_pbm: unknown): void {}

  // game/fingerhandler.cpp:668-692 — the unit-title hold flourish; the UnitMenu
  // form (new+Init(gpmfrmm, gpiniForms, kidfUnitMenu)+SetOwner+Show) is host
  // presentation behind CreateUnitTitle (null = the C++ alloc/Init failure arms)
  private ShowUnitTitle(pgob: Gob | null): void {
    // Must be a unit gob
    if (pgob === null || (pgob.GetFlags() & kfGobUnit) === 0) {
      return;
    }

    // Don't show this twice
    if (this.m_pfrmUnitTitle !== null) {
      return;
    }

    // Create the menu; initialize and show, non-modal
    this.m_pfrmUnitTitle = this.m_psui.host.CreateUnitTitle(pgob);
    if (this.m_pfrmUnitTitle === null) {
      return;
    }
    this.m_psui.host.PlaySfx(ksfxGuiFormShow); // gsndm.PlaySfx(ksfxGuiFormShow)
  }

  // game/fingerhandler.cpp:694-764 — apply the drag rect to the world: every
  // active unit gob gets Select(fSelect) — the sim's own selection mutator
  // (kfGobSelected is presentation-masked but SAVED state, mutated exactly).
  private SetSelection(): void {
    if (this.m_pselspr === null) {
      return;
    }

    const wptView: WPoint = { wx: 0, wy: 0 };
    this.m_psui.GetViewPos(wptView); // gsim.GetViewPos

    const drc = this.m_pselspr.GetDragRect(); // const DragRect& (no copy)
    const rcBounding = new Rect();
    drc.GetBoundingRect(rcBounding);

    // Rotate the rect since drc is rotated

    let t = rcBounding.left;
    rcBounding.left = rcBounding.top;
    rcBounding.top = t;
    t = rcBounding.right;
    rcBounding.right = rcBounding.bottom;
    rcBounding.bottom = t;

    const c = this.m_psui.gctx.coords;
    const ctx = this.m_psui.ctx;
    for (let pgobT = ctx.gobm.GetFirstGob(); pgobT !== null; pgobT = ctx.gobm.GetNextGob(pgobT)) {
      // Only unit gobs that are active
      const ff = pgobT.GetFlags();
      if ((ff & (kfGobUnit | kfGobActive)) !== (kfGobUnit | kfGobActive)) {
        continue;
      }
      const punt = pgobT as UnitGob;

      // Player must own this unit, or the game must be in god mode
      let fSelect = true;
      if (punt.GetOwner() !== ctx.plrLocal && !ctx.fGodMode) {
        fSelect = false;
      }

      // No structures unless it's a tower
      if (ff & kfGobStructure && (punt.GetConsts().um & kumTowers) === 0) {
        fSelect = false;
      }

      // (the #if 0 "no miners" arm is compiled out)

      // If center not in bounding rect, discard
      const wptCenter: WPoint = { wx: 0, wy: 0 };
      punt.GetCenter(wptCenter);
      const ptCenterX = c.PcFromWc(wptCenter.wx - wptView.wx);
      const ptCenterY = c.PcFromWc(wptCenter.wy - wptView.wy);
      if (!rcBounding.PtIn(ptCenterX, ptCenterY)) {
        fSelect = false;
      }

      // Center must be in drag rect
      if (fSelect) {
        const pt: DPoint = { x: ptCenterY, y: ptCenterX };
        if (!drc.PtIn(pt)) {
          fSelect = false;
        }
      }
      punt.Select(fSelect);
    }
  }
}
